import { useTranslation } from "react-i18next";
import { Users, ClipboardList, ListFilter, ArrowDownUp } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  OpenInspectionsFilterState,
  OpenInspectionsSortKey,
} from "@/lib/openInspectionsFilterStore";
import type { InspectionStatus } from "@/lib/openInspectionsWindow";

export interface FilterOption {
  id: string;
  name: string;
}

interface OpenInspectionsFilterBarProps {
  filters: OpenInspectionsFilterState;
  groups: FilterOption[];
  checklists: FilterOption[];
  onChange: (next: Partial<OpenInspectionsFilterState>) => void;
}

/** Open statuses offered as filter options (cancelled/completed excluded — GAP). */
const STATUS_OPTIONS: readonly InspectionStatus[] = ["pending", "in_progress", "overdue"];
const SORT_OPTIONS: readonly OpenInspectionsSortKey[] = ["due_date", "status", "created_at"];

const statusLabelKey = (status: InspectionStatus): string =>
  status === "in_progress" ? "inProgress" : status;

/**
 * Filter/sort bar for the "Offene Prüfungen" view (TES-506 §3).
 * Filter by Gruppe (TES-2017) / Status / Vorlage; sort by due_date / status / created_at.
 * State persistence (localStorage) lives in the parent panel (GAP 4).
 */
export function OpenInspectionsFilterBar({
  filters,
  groups,
  checklists,
  onChange,
}: OpenInspectionsFilterBarProps) {
  const { t } = useTranslation();

  return (
    <div
      className="flex flex-wrap items-center gap-2 md:gap-3"
      data-testid="open-inspections-filter-bar"
    >
      {/* Gruppe (TES-2017). data-testid kept stable (…-filter-team) for the e2e contract. */}
      <Select value={filters.groupId} onValueChange={(value) => onChange({ groupId: value })}>
        <SelectTrigger
          className="w-[140px] md:w-[180px] text-xs md:text-sm"
          aria-label={t("openInspections.filters.group")}
          data-testid="open-inspections-filter-team"
        >
          <Users className="mr-1 md:mr-2 h-3 w-3 md:h-4 md:w-4 shrink-0" />
          <SelectValue placeholder={t("openInspections.filters.group")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t("openInspections.filters.allGroups")}</SelectItem>
          {groups.map((group) => (
            <SelectItem key={group.id} value={group.id}>
              {group.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Status */}
      <Select
        value={filters.status}
        onValueChange={(value) =>
          onChange({ status: value as OpenInspectionsFilterState["status"] })
        }
      >
        <SelectTrigger
          className="w-[140px] md:w-[180px] text-xs md:text-sm"
          aria-label={t("openInspections.filters.status")}
          data-testid="open-inspections-filter-status"
        >
          <ClipboardList className="mr-1 md:mr-2 h-3 w-3 md:h-4 md:w-4 shrink-0" />
          <SelectValue placeholder={t("openInspections.filters.status")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t("dashboard.allStatuses")}</SelectItem>
          {STATUS_OPTIONS.map((status) => (
            <SelectItem key={status} value={status}>
              {t(`inspections.status.${statusLabelKey(status)}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Vorlage (checklist) */}
      <Select
        value={filters.checklistId}
        onValueChange={(value) => onChange({ checklistId: value })}
      >
        <SelectTrigger
          className="w-[140px] md:w-[180px] text-xs md:text-sm"
          aria-label={t("openInspections.filters.template")}
          data-testid="open-inspections-filter-template"
        >
          <ListFilter className="mr-1 md:mr-2 h-3 w-3 md:h-4 md:w-4 shrink-0" />
          <SelectValue placeholder={t("openInspections.filters.template")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t("openInspections.filters.allTemplates")}</SelectItem>
          {checklists.map((checklist) => (
            <SelectItem key={checklist.id} value={checklist.id}>
              {checklist.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Sort */}
      <Select
        value={filters.sortBy}
        onValueChange={(value) => onChange({ sortBy: value as OpenInspectionsSortKey })}
      >
        <SelectTrigger
          className="w-[140px] md:w-[180px] text-xs md:text-sm"
          aria-label={t("openInspections.filters.sortBy")}
          data-testid="open-inspections-filter-sort"
        >
          <ArrowDownUp className="mr-1 md:mr-2 h-3 w-3 md:h-4 md:w-4 shrink-0" />
          <SelectValue placeholder={t("openInspections.filters.sortBy")} />
        </SelectTrigger>
        <SelectContent>
          {SORT_OPTIONS.map((key) => (
            <SelectItem key={key} value={key}>
              {t(`openInspections.sort.${key}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
